/**
 * Lighthouse data hooks — React Query wrappers around the Lighthouse
 * Service API. Adapter functions map the snake_case DTOs onto the
 * existing UI types in `src/types/lighthouse.ts`.
 */
import { useQuery } from "@tanstack/react-query";
import { lighthouseGet, LighthouseApiError } from "@/services/lighthouseApi";
import { edrGet, EdrApiError } from "@/services/edrApi";
import { useConnectorEnabled } from "@/hooks/connectors/useConnector";

/**
 * Treat 404/405 (missing route) and 403 (legacy forbidden on retired
 * Lighthouse paths) as "feature not yet wired" — return the empty
 * fallback rather than surfacing an error.
 */
function isMissingRoute(err: unknown): boolean {
  const status =
    err instanceof EdrApiError || err instanceof LighthouseApiError ? err.status : 0;
  return status === 404 || status === 405 || status === 403;
}
import type {
  AgentSummaryDTO,
  AgentDetailResponseDTO,
  SubIdentityDTO,
  AttestationSummaryDTO,
  LhClassification,
  McpServerDTO,
  ConnectorStatusDTO,
  InventorySummaryDTO,
  LlmEndpointDTO,
  CredentialAccessDTO,
} from "@/types/lighthouseApi";
import type {
  LighthouseAgent,
  AgentClassification,
  RiskFactors,
  IdentityBinding,
  Attestation,
  AgentObservation,
} from "@/types/lighthouse";

// ---------- Mapping ----------

const CLASS_MAP: Record<LhClassification, AgentClassification> = {
  confirmed: "sanctioned",
  probable: "unknown",
  possible: "shadow",
  not_agent: "retired",
};

function bandedRisk(score: number | null | undefined): number {
  if (typeof score !== "number" || Number.isNaN(score)) return 0;
  return Math.max(0, Math.min(100, Math.round(score)));
}

function defaultFactors(score: number): RiskFactors {
  // List endpoint only returns the composite score; spread it as a flat
  // approximation so the existing radar/score UI stays meaningful.
  return { reach: score, exposure: score, behavior: score, posture: score };
}

function adaptAgentSummary(a: AgentSummaryDTO): LighthouseAgent {
  const score = bandedRisk(a.risk_score);
  return {
    id: a.agent_id,
    name: a.display_name,
    vendor: a.canonical_publisher ?? "unknown",
    classification: CLASS_MAP[a.classification] ?? "unknown",
    riskScore: score,
    riskFactors: defaultFactors(score),
    owner: undefined,
    planes: a.device_id ? ["endpoint"] : [],
    firstSeen: a.first_seen_at ?? new Date().toISOString(),
    lastSeen: a.last_seen_at ?? new Date().toISOString(),
    attestationId: a.attestation_present ? a.agent_id : undefined,
  };
}

function adaptAgentDetail(detail: AgentDetailResponseDTO): {
  agent: LighthouseAgent;
  factors: RiskFactors;
  bindings: IdentityBinding[];
  observations: AgentObservation[];
  attestation?: Attestation;
} {
  const base = adaptAgentSummary(detail.agent);
  const rf = detail.risk_factors;
  const factors: RiskFactors = rf
    ? {
        reach: Math.round((rf.reach?.score ?? base.riskScore) * (rf.reach?.weight ?? 1) * 100) /
          (rf.reach?.weight ? 100 : 1) || bandedRisk(rf.reach?.score),
        exposure: bandedRisk(rf.exposure?.score),
        behavior: bandedRisk(rf.behavior?.score),
        posture: bandedRisk(rf.posture?.score),
      }
    : base.riskFactors;

  // Normalise extreme rounding above — fall back to raw subscore when the
  // weighted compose comes out >100.
  if (factors.reach > 100 || factors.reach < 0) factors.reach = bandedRisk(rf?.reach?.score);

  const bindings: IdentityBinding[] = (detail.sub_identities ?? []).map((s) =>
    adaptSubIdentity(s),
  );

  const observations: AgentObservation[] = (detail.sub_identities ?? []).map((s, i) => ({
    id: s.id ?? `obs_${i}`,
    plane: (s.plane as AgentObservation["plane"]) || "endpoint",
    ts: s.last_seen_at ?? s.first_seen_at ?? new Date().toISOString(),
    source: s.source ?? s.plane ?? "unknown",
    detail: s.display_name ?? s.identifier,
  }));

  const firstAttestation = detail.bound_attestations?.[0];
  const attestation = firstAttestation ? adaptAttestation(firstAttestation) : undefined;

  return { agent: { ...base, riskFactors: factors }, factors, bindings, observations, attestation };
}

const BINDING_TYPE_MAP: Record<string, IdentityBinding["type"]> = {
  idp: "idp_user",
  okta: "idp_user",
  workspace: "idp_user",
  entra: "idp_user",
  github: "github_app",
  aws: "iam_role",
  oauth: "oauth_grant",
};

function adaptSubIdentity(s: SubIdentityDTO): IdentityBinding {
  const planeKey = (s.plane ?? "").toLowerCase();
  let type: IdentityBinding["type"] = "idp_user";
  for (const k of Object.keys(BINDING_TYPE_MAP)) {
    if (planeKey.includes(k)) {
      type = BINDING_TYPE_MAP[k];
      break;
    }
  }
  return {
    principal: s.display_name ?? s.identifier,
    type,
    confidence:
      s.confidence === "deterministic" || s.confidence === "probabilistic"
        ? s.confidence
        : "probabilistic",
    scopes: s.scopes ?? [],
  };
}

function adaptAttestation(a: AttestationSummaryDTO): Attestation {
  return {
    id: a.id,
    publisher: a.manifest_publisher ?? "unknown",
    agentName: a.manifest_name ?? "—",
    scopes: [],
    owner: a.manifest_publisher ?? "—",
    validated: a.status === "bound",
    registeredAt: a.first_seen_at ?? new Date().toISOString(),
  };
}

// ---------- Hooks ----------

const STALE_MS = 30_000;

export function useLighthouseAgents() {
  const enabled = useConnectorEnabled("lighthouse");
  return useQuery({
    enabled,
    queryKey: ["lighthouse", "agents"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<LighthouseAgent[]> => {
      const { data } = await lighthouseGet<AgentSummaryDTO[]>("/api/v1/lighthouse/agents");
      return (data ?? []).map(adaptAgentSummary);
    },
  });
}

export function useLighthouseAgent(agentId: string | undefined) {
  const enabled = useConnectorEnabled("lighthouse") && !!agentId;
  return useQuery({
    enabled,
    queryKey: ["lighthouse", "agent", agentId],
    staleTime: STALE_MS,
    queryFn: async () => {
      const { data } = await lighthouseGet<AgentDetailResponseDTO>(
        `/api/v1/lighthouse/agents/${agentId}`,
      );
      return adaptAgentDetail(data);
    },
  });
}

export function useLighthouseAttestations() {
  const enabled = useConnectorEnabled("lighthouse");
  return useQuery({
    enabled,
    queryKey: ["lighthouse", "attestations"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<Attestation[]> => {
      const { data } = await lighthouseGet<AttestationSummaryDTO[]>(
        "/api/v1/lighthouse/attestations",
      );
      return (data ?? []).map(adaptAttestation);
    },
  });
}

// ---------- Pass 2 read surfaces ----------

export function useLighthouseMcpServers() {
  const enabled = useConnectorEnabled("lighthouse");
  return useQuery({
    enabled,
    queryKey: ["lighthouse", "mcp", "servers"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<McpServerDTO[]> => {
      // Per backend INTEGRATION-GAPS-RESPONSE: MCP server inventory now
      // lives on the EDR connector at /mcp/endpoints (+ /mcp/processes
      // for per-host detail). We surface endpoints as "servers".
      try {
        const data = await edrGet<
          McpServerDTO[] | { endpoints?: McpServerDTO[]; servers?: McpServerDTO[] }
        >("/api/v1/connectors/edr/mcp/endpoints");
        if (Array.isArray(data)) return data;
        const obj = data as { endpoints?: McpServerDTO[]; servers?: McpServerDTO[] } | undefined;
        return obj?.endpoints ?? obj?.servers ?? [];
      } catch (err) {
        if (isMissingRoute(err)) return [];
        throw err;
      }
    },
  });
}

/**
 * Per-plane connector health. Backend renamed the singular
 * `/connectors/{name}/status` to a plural `/connectors` list — we fetch
 * the list and pick the matching connector.
 */
export function useConnectorPlaneStatus(name: string | undefined) {
  const enabled = useConnectorEnabled("lighthouse") && !!name;
  return useQuery({
    enabled,
    queryKey: ["lighthouse", "connectors", name, "status"],
    staleTime: 15_000,
    queryFn: async (): Promise<ConnectorStatusDTO> => {
      try {
        const { data } = await lighthouseGet<
          | ConnectorStatusDTO[]
          | { connectors?: ConnectorStatusDTO[] }
          | ConnectorStatusDTO
        >("/api/v1/lighthouse/connectors");
        const list: ConnectorStatusDTO[] = Array.isArray(data)
          ? data
          : ((data as { connectors?: ConnectorStatusDTO[] } | undefined)?.connectors ?? []);
        const match = list.find(
          (c) =>
            (c as { name?: string; id?: string }).name === name ||
            (c as { name?: string; id?: string }).id === name,
        );
        return match ?? (Array.isArray(data) ? {} : ((data as ConnectorStatusDTO) ?? {}));
      } catch (err) {
        if (isMissingRoute(err)) return {};
        throw err;
      }
    },
  });
}

// ---------- Inventory summary (P2) ----------

/**
 * Inventory roll-up that powers the rebuilt Inventory page header.
 * Sourced from the EDR connector's `/research/scorecard` per backend
 * INTEGRATION-GAPS-RESPONSE. Returns `null` on missing-route so the
 * page falls back to client-side aggregation.
 */
export function useLighthouseInventorySummary() {
  const enabled = useConnectorEnabled("lighthouse");
  return useQuery({
    enabled,
    queryKey: ["lighthouse", "inventory", "summary"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<InventorySummaryDTO | null> => {
      try {
        const data = await edrGet<InventorySummaryDTO>(
          "/api/v1/connectors/edr/research/scorecard",
        );
        return data ?? null;
      } catch (err) {
        if (isMissingRoute(err)) return null;
        throw err;
      }
    },
  });
}

/** Discovered LLM endpoints called by agents. Now on EDR connector. */
export function useLighthouseLlmEndpoints() {
  const enabled = useConnectorEnabled("lighthouse");
  return useQuery({
    enabled,
    queryKey: ["lighthouse", "llm", "endpoints"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<LlmEndpointDTO[]> => {
      try {
        const data = await edrGet<LlmEndpointDTO[] | { endpoints?: LlmEndpointDTO[] }>(
          "/api/v1/connectors/edr/lighthouse/llm-endpoints",
        );
        if (Array.isArray(data)) return data;
        return (data as { endpoints?: LlmEndpointDTO[] } | undefined)?.endpoints ?? [];
      } catch (err) {
        if (isMissingRoute(err)) return [];
        throw err;
      }
    },
  });
}

/** Credential file access events. Now on EDR connector. */
export function useLighthouseCredentialAccess() {
  const enabled = useConnectorEnabled("lighthouse");
  return useQuery({
    enabled,
    queryKey: ["lighthouse", "credential", "access"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<CredentialAccessDTO[]> => {
      try {
        const data = await edrGet<
          CredentialAccessDTO[] | { events?: CredentialAccessDTO[] }
        >("/api/v1/connectors/edr/lighthouse/credential-access");
        if (Array.isArray(data)) return data;
        return (data as { events?: CredentialAccessDTO[] } | undefined)?.events ?? [];
      } catch (err) {
        if (isMissingRoute(err)) return [];
        throw err;
      }
    },
  });
}
